"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaselineProxy = void 0;
const timespans_1 = require("../utils/timespans");
const guard_1 = require("../utils/guard");
const timer_1 = require("../utils/timer");
const utils_1 = require("./utils");
/**
 * A proxy that calculates an alarm level and sends warnings to logs.
 */
class BaselineProxy {
    /**
     * Initializes a new instance of the @see BaselineProxy class.
     * @param startSamplingAfter A timespan after when sampling should start. Use this if server needs a longer time to start.
     * @param maxSampleDuration A timespan within samples should be gathered after sampling start.
     * @param maxSamplesCount The maximum number of samples.
     * @param logger Logger to use.
     */
    constructor(startSamplingAfter = timespans_1.TimeSpansInMilliSeconds.tenMinutes, maxSampleDuration = timespans_1.TimeSpansInMilliSeconds.tenMinutes, maxSamplesCount = 100, logger) {
        guard_1.Guard.throwIfNullOrNegative(startSamplingAfter, 'startSamplingAfter');
        guard_1.Guard.throwIfNullOrNegative(maxSampleDuration, 'maxSampleDuration');
        guard_1.Guard.throwIfNullOrNegative(maxSamplesCount, 'maxSamplesCount');
        guard_1.Guard.throwIfNullOrEmpty(logger, 'logger');
        const now = new Date();
        this.startSamplingAfter = new Date(now.getTime() + startSamplingAfter);
        this.samples = [];
        this.maxSamplesCount = maxSamplesCount;
        this.maxSampleDuration = new Date(now.getTime() + startSamplingAfter + maxSampleDuration);
        this.isSamplingFinished = false;
        this.alarmLevel = 0;
        this.logger = logger;
        this.avg = 0;
        this.min = Number.MAX_SAFE_INTEGER;
        this.max = Number.MIN_SAFE_INTEGER;
    }
    /**
     * Executes a function within a resilience proxy.
     * @param func Function to execute within the resilience proxy.
     * @param requestId Request Guid.
     * @returns The result of the executed function.
     */
    execute(func, requestId) {
        return __awaiter(this, void 0, void 0, function* () {
            const durationTimer = timer_1.timer();
            const result = yield func();
            const milliseconds = durationTimer.milliSeconds;
            this.sample(milliseconds);
            if (this.isSamplingFinished) {
                if (this.alarmLevel > 0 && milliseconds >= this.alarmLevel) {
                    this.logger.warning(requestId, `returned above alarm level in ${milliseconds}/${this.alarmLevel}ms ${this.getStatisticsString()}`, null, utils_1.logFormatter);
                }
                else {
                    this.logger.information(requestId, `returned in ${milliseconds}/${this.alarmLevel}ms ${this.getStatisticsString()}`, null, utils_1.logFormatter);
                }
            }
            else {
                this.logger.information(requestId, `returned in ${milliseconds}ms. Sampling ongoing.`, null, utils_1.logFormatter);
            }
            return result;
        });
    }
    /**
     * Gets a readable string from the statistics.
     * @returns A readable string from the statistics.
     */
    getStatisticsString() {
        return `AVG = ${this.avg} MIN = ${this.min} MAX = ${this.max}`;
    }
    /**
     * Does the sampling and statistics calculation.
     * @param value Sample value to add.
     */
    sample(value) {
        if (this.isSamplingFinished) {
            if (this.avg === 0) {
                this.avg = value;
            }
            else {
                this.avg += value;
                this.avg = Math.ceil((this.avg /= 2));
            }
            if (value < this.min) {
                this.min = value;
            }
            if (value > this.max) {
                this.max = value;
            }
            return;
        }
        const now = new Date().getTime();
        if (now < this.startSamplingAfter.getTime()) {
            return;
        }
        if (now > this.maxSampleDuration.getTime() ||
            this.samples.length >= this.maxSamplesCount) {
            this.calculateAlarmLevel();
            this.isSamplingFinished = true;
        }
        else {
            this.samples.push(value);
        }
    }
    /**
     * Calculates the alarm level.
     */
    calculateAlarmLevel() {
        const mean = this.calculateMean();
        const sdev = this.calculateStandardDeviation(mean);
        const level = 1.2 * mean + 3 * sdev;
        if (!level) {
            this.alarmLevel = 0;
        }
        else {
            this.alarmLevel = Math.floor(level);
        }
    }
    /**
     * Calculates the mean of the samples.
     * @returns The mean of the samples.
     */
    calculateMean() {
        if (this.samples.length > 0) {
            return this.samples.reduce((p, c) => p + c, 0) / this.samples.length;
        }
        return 0;
    }
    /**
     * Calculates the standard deviation of the samples.
     * @param mean Mean value of samples.
     * @returns The standard deviation of the samples.
     */
    calculateStandardDeviation(mean) {
        if (this.samples.length <= 0) {
            return 0;
        }
        const variance = this.calculateVariance(mean);
        return Math.sqrt(variance);
    }
    /**
     * Calculates the variance of the samples.
     * @param mean Mean value of samples.
     * @returns The variance of the samples.
     */
    calculateVariance(mean) {
        if (this.samples.length <= 0) {
            return 0;
        }
        let variance = 0;
        for (let i = 0; i < this.samples.length; i++) {
            variance += Math.pow(this.samples[i] - mean, 2);
        }
        variance /= this.samples.length - 1;
        return variance;
    }
}
exports.BaselineProxy = BaselineProxy;
//# sourceMappingURL=baselineProxy.js.map